import { find, get } from "lodash";
import { Resizable } from "re-resizable";
import React, { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Switch, useHistory, useLocation, useParams } from "react-router";
import { AppBarLarge } from "src/components/AppBar/AppBar";
import { IconName } from "src/components/Icon/Icon";
import Toast from "src/components/Toast/Toast";
import { useLoading } from "src/hooks/useLoading";
import { UserRole } from "src/store/models/user.model";
import { IRootState } from "src/store/reducers";
import { fetchApplicationAgents } from "src/store/reducers/applicationAgents";
import { appBuilderShouldSave, fetchApplicationOptions } from "src/store/reducers/applicationBuilder";
import { saveProductDetails } from "src/store/reducers/applicationProduct";
import {
  createApplication,
  fetchApplication,
  hideErrorToastApplication,
  updateApplication,
} from "src/store/reducers/applications";
import { hasAnyAuthority } from "src/util/roleAuthorization";
import { addDefaultOption } from "src/util/utils.defaultValues";
import { Container } from "../../components/Container/Container";
import { Tabs } from "../../components/NavTabs/Tabs";
import * as S from "../../constants/StringConstants";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { AppBarInfoBox } from "./AppBarInfoBox";
import "./ApplicationBuilder.scss";
import { PageContainer } from "./PageContainer";
import { pages } from "./pages";
import { AppClientInfo } from "./sections";
import AppAlertModal from "./sections/alerts/AppAlertModal/AppAlertModal";
import { validate } from "./validator";

const VerticalContainer = props => {
  return <div className="vertical-container">{props.children}</div>;
};

export const ApplicationBuilder = props => {
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [changeToURL, setChangeToURL] = useState<string | null>(null);
  const [tabDirty, setTabDirty] = useState(false);
  const [tabValid, setTabValid] = useState(false);
  const [showWarningOCR, setShowWarningOCR] = useState(false);
  const [tabErrors, setTabErrors] = useState<Record<string, any>>([]);
  const location = useLocation();
  const history = useHistory();

  const { id } = useParams<{ id: string }>();
  const [clientInfoHeight, setClientInfoHeight] = useLocalStorage("clientInfoHeight", null);
  const [pinnedTab, setPinnedTab] = useLocalStorage<string | null>("pinnedTab", null);

  // Highlight Active Tab
  const pathNameRegex = /\/([a-zA-Z0-9\-]+)$/;
  const match = pathNameRegex.exec(location.pathname);
  const pathname = match ? match[1] : "";

  const isNew = id === "new";
  const isBack = history.action === "POP" && history.length > 1;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchApplicationOptions());
    if (!isNew) {
      dispatch(fetchApplication(id));
      dispatch(fetchApplicationAgents(id));
    }
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(fetchApplication(id));
    }
  }, [id]);

  const { isLoading, setIsLoading } = useLoading();

  const { appBuilder, app, applications, auth } = useSelector((state: IRootState) => {
    return {
      app: state.applications.map[id],
      appBuilder: state.applications.map[id],
      applications: state.applications,
      auth: state.auth,
    };
  });

  const isFetching = useSelector((state: IRootState) => state.applicationBuilder.loading);

  const {
    carrierOptions,
    branchOptions,
    statusOptions,
    uwOptions,
    uwSpecialistID,
    stateOptions,
    genderOptions,
    productOptions,
    agents,
  } = useSelector((state: IRootState) => ({
    loading: state.applicationBuilder.loading,
    carrierOptions: state.applicationBuilder.selectCarrierTypeList,
    branchOptions: state.applicationBuilder.selectBranchList,
    statusOptions: state.applicationBuilder.selectStatusList,
    uwOptions: state.applicationBuilder.selectUWList,
    uwSpecialistID: state.applicationBuilder.uwSpecialistID,
    productOptions: state.applicationBuilder.selectProductList,
    stateOptions: state.applicationBuilder.selectStateList,
    genderOptions: state.applicationBuilder.selectGenderList,
    agents: state.applicationAgents.agents,
  }));

  // get app data
  const appAlerts = get(app, "applicationAlerts");
  const selectedState = find(stateOptions, { value: app?.stateID });
  const selectedProduct = find(productOptions, { value: app?.productId });
  const defaultStatus = statusOptions.find(option => option.key === "App. Submitted");

  // TODO: remove this mapping, try to make the fields use same names as api names
  const initialValues = {
    ...(!isNew ? { id } : { uwSpecialistID }), // include application id, so update can work. when new, use default uwSpecialistID
    ...app,
    firstName: app?.firstName,
    middleName: app?.middleName,
    lastName: app?.lastName,
    addressLine1: app?.addressLine1,
    addressLine2: app?.addressLine2,
    addressLine3: app?.addressLine3,
    nameSuffix: app?.nameSuffix,
    city: app?.city,
    dob: app?.dateOfBirth,
    ssn: app?.ssn,
    email: app?.email,
    policy: app?.policyNumber,
    paidOrClosedDate: app?.paidOrClosedDate,
    //effectiveDate: app?.signedDate,
    deliveryOrderDate: app?.deliveryOrderDate,
    deliveryDueDate: app?.deliveryDueDate,
    reviewDate: app?.reviewDate,
    zip: app?.zip,
    carrierId: app?.carrierId,
    productId: app?.productId,
    policyNumber: app?.policyNumber,
    state: get(selectedState, "key", ""),
    dlState: get(app, "driverLicenseStateID"),
    dlNumber: get(app, "driverLicenseNumber"),
    issueAge: get(app, "issueAge"),
    statusId: !isNew ? get(app, "statusId") : defaultStatus?.value,
    branchTypeID: get(app, "branchTypeID"),
    selectProductType: selectedProduct?.typeDesc,
    generatedByOCR: false, // set this to clear this flag on save
  };

  useEffect(() => {
    if (app?.generatedByOCR) {
      setShowWarningOCR(true);
    }
  }, [app?.generatedByOCR]);

  useEffect(() => {
    if (appAlerts?.length > 0 && !isBack) {
      setIsAlertModalOpen(true);
    }
  }, [appAlerts]);

  const handleSave = async values => {
    setIsLoading(true);
    let appId = id;

    if (isNew) {
      const res: any = await dispatch(createApplication(values));
      appId = res.value.data;
      // hack! save just the  IO flag to productDetails when creating the new application from convert proposal
      await dispatch(
        saveProductDetails(appId, {
          applicationID: appId,
          increaseOption: values.increaseOption,
        }),
      );
    } else {
      delete values.applicationAlerts;
      await dispatch(updateApplication(values, id));
    }
    await dispatch(appBuilderShouldSave(true, appId));
    await dispatch(fetchApplication(appId));
    await dispatch(fetchApplicationAgents(appId));
    setIsLoading(false);
    if (isNew) {
      history.replace(`/applications/${appId}`);
    }
  };

  const handleCancel = async (dirty: boolean) => {
    if (dirty) {
      if (confirm(S.APP_DISCARD_CHANGES)) {
        history.push("/applications");
      }
    } else {
      history.push("/applications");
    }
  };

  const isReadonly = hasAnyAuthority(auth.user, [UserRole.BranchManager]);
  const primaryAgent = agents?.find(agent => agent.primary);

  return (
    <div className="app-builder">
      <Toast message={S.LOADING} onClose={() => {}} open={isLoading} />
      <Toast
        message={applications.errorMessage}
        onClose={() => {
          dispatch(hideErrorToastApplication());
        }}
        open={applications.error}
      />

      <Form onSubmit={handleSave} initialValues={initialValues} validate={validate}>
        {({ handleSubmit, values, dirty, valid, errors, form: mainForm }) => (
          <form onSubmit={handleSubmit}>
            <VerticalContainer>
              <AppBarLarge title={S.APP_NEW_TITLE} icon={IconName.Applications} loading={isFetching}>
                <AppBarInfoBox
                  isReadonly={isReadonly}
                  isNew={isNew}
                  agentName={primaryAgent?.agentFullName}
                  agentPhone={primaryAgent?.agentPhone}
                  agentEmail={primaryAgent?.agentEmail}
                  caseManager={app?.uwSpecialist}
                  proposalID={app?.proposalID}
                  proposalSpecialist={app?.proposalSpecialistName}
                  orgOptions={addDefaultOption(S.APP_BUILDER_ORGANIZATION_SELECT_LABEL, branchOptions)}
                  hierarchyName={primaryAgent?.agentAffiliateName}
                  userOptions={addDefaultOption("", uwOptions)}
                />
              </AppBarLarge>

              <Toast message={S.APP_OCR_WARNING} onClose={() => setShowWarningOCR(false)} open={showWarningOCR} />

              {!isFetching && (
                <>
                  <Container>
                    <Resizable
                      handleComponent={{ bottom: <div className="drag-handle" /> }}
                      defaultSize={{
                        height: clientInfoHeight,
                      }}
                      enable={{ bottom: true }}
                      onResizeStop={(e, direction, ref, d) => {
                        setClientInfoHeight(ref.offsetHeight);
                      }}>
                      <div
                        {...{ inert: isReadonly ? "" : undefined }}
                        style={{
                          overflow: "scroll",
                          height: "100%",
                          width: "100%",
                        }}>
                        <AppClientInfo
                          stateOptions={stateOptions}
                          carrierOptions={carrierOptions}
                          statusOptions={statusOptions}
                          productOptions={productOptions}
                          genderOptions={genderOptions}
                          onStatusHistoryClick={e => {
                            e.preventDefault();
                            history.push(`/applications/${id}/status-history`);
                          }}
                        />
                      </div>
                    </Resizable>
                  </Container>

                  <Tabs
                    pages={pages}
                    pathname={pathname}
                    isNew={isNew}
                    setChangeToURL={setChangeToURL}
                    id={id}
                    pinnedTab={pinnedTab}
                    setPinnedTab={setPinnedTab}
                    basePath="applications"
                  />

                  <Container>
                    <Switch>
                      {pages.map((tab, index) => (
                        <Route
                          key={index}
                          path={`/applications/:id/${tab.path}`}
                          render={props => {
                            const { component: Component } = tab;
                            const mainDirty = dirty;
                            const enableSave = (mainDirty || tabDirty) && valid && tabValid;
                            let saveTooltip = "No changes";

                            const errs = [
                              ...(!valid ? Object.values(errors) : []),
                              ...(!tabValid ? Object.values(tabErrors) : []),
                            ].filter(x => x.length > 0); // this will filter empty arrays and strings
                            if (!enableSave && errs.length > 0) {
                              // TODO move this string to constants
                              saveTooltip = "Fields failing validation: " + errs.join(", ");
                            }

                            return (
                              <PageContainer
                                enableSave={enableSave}
                                onCancel={() => handleCancel(dirty)}
                                isReadonly={isReadonly}
                                saveTooltip={saveTooltip}>
                                <Component
                                  {...(props as any)}
                                  isReadonly={isReadonly}
                                  dirty={tabDirty}
                                  onDirty={dirty => setTabDirty(dirty)}
                                  onValid={(valid, errors) => {
                                    setTabValid(valid);
                                    setTabErrors(errors);
                                  }}
                                  changeToURL={changeToURL}
                                  onChangeURL={() => setChangeToURL(null)}
                                  validate={
                                    tab.path === "product"
                                      ? values => {
                                          mainForm.change("entryDate", values.entryDate);
                                          mainForm.change("signedDate", values.signedDate);
                                          return {};
                                        }
                                      : undefined
                                  }
                                />
                              </PageContainer>
                            );
                          }}
                        />
                      ))}
                      {/* open agents for new applications */}
                      {/* open first tab or pinned when none selected */}
                      <Redirect
                        to={{
                          pathname: `/applications/${id}/${pinnedTab && !isNew ? pinnedTab : pages[0].path}`,
                          state: location.state,
                        }}
                      />
                    </Switch>
                  </Container>
                </>
              )}
            </VerticalContainer>
          </form>
        )}
      </Form>

      {appAlerts?.length > 0 && (
        <AppAlertModal open={isAlertModalOpen} alerts={appAlerts} onClose={() => setIsAlertModalOpen(false)} />
      )}
    </div>
  );
};

export default ApplicationBuilder;
